/**
 * Hierarquia visual do status da proposta — espelho do helper equivalente
 * em `features/leads/lead-status-style.ts`. Mantido em arquivo separado
 * pra evitar dependência circular (proposals importa leads ao contrário
 * fica mais natural).
 *
 * Reusa os mesmos níveis (`StatusVisualLevel`) e classes — só muda o
 * mapeamento status → nível.
 *
 * **Marcos do funil** (level='milestone'):
 *   - `accepted` → ✅ "Aceita" — proposta aceita pelo lead, momento
 *     mais importante da etapa de proposta. Card ganha borda lateral
 *     accent + bg sutil pra Karla bater o olho e identificar na lista.
 *
 * **Aguardando lead** (level='waiting'):
 *   - `sent`, `viewed`, `in_negotiation` — proposta em aberto, esperando
 *     decisão do lead. Outline warning amarelo deixa Karla ver "ainda
 *     não fechou" sem pressão visual.
 *
 * **Perdidos** (level='lost'): rejected, expired.
 */

import type { ProposalStatus } from './schemas.js';
import { type StatusVisualStyle, type StatusVisualLevel } from '../leads/lead-status-style.js';

// Re-export pros consumers terem o tipo sem precisar importar de leads.
export type { StatusVisualStyle, StatusVisualLevel };

/** Espelho exato do `LEVEL_STYLES` em lead-status-style.ts — duplicado
 * de propósito pra evitar import cycle. Mudar o visual = mudar nos
 * dois lugares (~10 linhas de diff). Vale a pena pelo desacoplamento. */
const LEVEL_STYLES: Record<StatusVisualLevel, Omit<StatusVisualStyle, 'level' | 'icon'>> = {
  milestone: {
    badgeClassName:
      'inline-flex items-center gap-1.5 rounded-md border border-accent/60 bg-accent/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow-sm',
    cardClassName: 'border-l-4 border-l-accent bg-accent/[0.04]',
  },
  active: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/5 px-2 py-0.5 text-xs font-medium text-accent-foreground',
    cardClassName: '',
  },
  waiting: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-warning/40 bg-warning/5 px-2 py-0.5 text-xs font-medium text-foreground',
    cardClassName: '',
  },
  neutral: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground',
    cardClassName: '',
  },
  lost: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive',
    cardClassName: '',
  },
};

const PROPOSAL_STATUS_LEVEL: Record<
  ProposalStatus,
  { level: StatusVisualLevel; icon: string | null }
> = {
  draft: { level: 'neutral', icon: null },
  sent: { level: 'waiting', icon: '⏳' },
  viewed: { level: 'waiting', icon: '👀' },
  in_negotiation: { level: 'waiting', icon: '💬' },
  // Marco do funil — proposta aceita pelo lead.
  accepted: { level: 'milestone', icon: '✅' },
  rejected: { level: 'lost', icon: null },
  expired: { level: 'lost', icon: null },
};

export function getProposalStatusStyle(status: ProposalStatus): StatusVisualStyle {
  const config = PROPOSAL_STATUS_LEVEL[status];
  const base = LEVEL_STYLES[config.level];
  return {
    level: config.level,
    icon: config.icon,
    badgeClassName: base.badgeClassName,
    cardClassName: base.cardClassName,
  };
}
